import { useState, useEffect } from 'react';
import { Clock, Filter, Search, CheckCircle, AlertCircle, User, Video, FileText, MessageSquare, Mail, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { projectActionsService } from '../../services/projectActions';
import { toast } from 'sonner';

interface ActionSourceDetails {
  type?: string;
  name?: string;
  date?: string;
  time?: string;
  id?: string | null;
  from?: string;
}

interface Action {
  id: string;
  title: string;
  assignedTo: string;
  dueDate: string;
  status: string;
  priority: string;
  source: string;
  sourceDetails: ActionSourceDetails;
}

function formatDate(d: string | null): string {
  if (!d) return '';
  return d.slice(0, 10);
}

function mapApiToAction(item: import('../../services/projectActions').ProjectActionItem): Action {
  const details = (item.source_details || {}) as ActionSourceDetails;
  return {
    id: `A-${String(item.id).padStart(3, '0')}`,
    title: item.title,
    assignedTo: item.assigned_to || '',
    dueDate: formatDate(item.due_date),
    status: item.status || 'Not Started',
    priority: item.priority || 'Medium',
    source: item.source || 'manual',
    sourceDetails: details,
  };
}

interface ActionsSectionProps {
  projectId?: number;
}

export function ActionsSection({ projectId }: ActionsSectionProps) {
  const [showCompleted, setShowCompleted] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['all']));
  const [actions, setActions] = useState<Action[]>([]);

  useEffect(() => {
    if (!projectId) {
      setActions([]);
      return;
    }
    projectActionsService
      .list(projectId)
      .then((list) => setActions(list.map(mapApiToAction)))
      .catch(() => {
        toast.error('Failed to load action items');
        // Do NOT fall back to demo data on real projects — see RisksSection.
        setActions([]);
      });
  }, [projectId]);

  const actionStats = {
    total: actions.length,
    notStarted: actions.filter(a => a.status === 'Not Started').length,
    inProgress: actions.filter(a => a.status === 'In Progress').length,
    completed: actions.filter(a => a.status === 'Completed').length,
    overdue: actions.filter(a => a.status === 'Overdue').length,
    critical: actions.filter(a => a.priority === 'Critical').length,
    fromMeetings: actions.filter(a => a.source === 'teams-meeting').length,
    fromEmails: actions.filter(a => a.source === 'outlook-email').length,
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-700 border-green-200';
      case 'In Progress': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Not Started': return 'bg-gray-100 text-gray-600 border-gray-200';
      case 'Overdue': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const getPriorityDot = (priority: string) => {
    switch (priority) {
      case 'Critical': return 'bg-red-500';
      case 'High': return 'bg-orange-500';
      case 'Medium': return 'bg-yellow-500';
      default: return 'bg-gray-400';
    }
  };

  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'teams-meeting': return <Video className="w-4 h-4" />;
      case 'outlook-email': return <Mail className="w-4 h-4" />;
      case 'decision': return <CheckCircle className="w-4 h-4" />;
      case 'issue': return <AlertCircle className="w-4 h-4" />;
      case 'manual': return <MessageSquare className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const getSourceColor = (source: string) => {
    switch (source) {
      case 'teams-meeting': return 'bg-blue-500';
      case 'outlook-email': return 'bg-indigo-500';
      case 'decision': return 'bg-green-500';
      case 'issue': return 'bg-orange-500';
      case 'manual': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const isOverdue = (dueDate: string, status: string) => {
    return status !== 'Completed' && new Date(dueDate) < new Date();
  };

  const filteredActions = showCompleted ? actions : actions.filter(a => a.status !== 'Completed');

  // Group actions by source for grouped view
  const groupedActions = filteredActions.reduce((acc, action) => {
    const key = `${action.sourceDetails.type}-${action.sourceDetails.id || action.sourceDetails.name}`;
    if (!acc[key]) {
      acc[key] = {
        source: action.source,
        sourceDetails: action.sourceDetails,
        actions: []
      };
    }
    acc[key].actions.push(action);
    return acc;
  }, {} as Record<string, { source: string; sourceDetails: any; actions: typeof actions }>);

  const toggleGroup = (key: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedGroups(newExpanded);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#486581] via-[#5c748e] to-[#486581] rounded-xl p-8 text-white border-b-4 border-[#DAA520]">
        <div className="mb-6">
          <h1 className="text-3xl mb-2">Action Items</h1>
          <p className="text-[#d4dce5]">Tracked from meetings, emails, decisions, and RAID items</p>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-4 gap-6">
          <div className="bg-white rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <div className="text-sm text-gray-600 mb-2">Total Actions</div>
            <div className="text-3xl font-medium text-neutral-950 mb-1">{actionStats.total}</div>
            <div className="text-sm text-gray-600">
              Across all sources
            </div>
          </div>

          <div className="bg-white rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-lg bg-orange-50 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-orange-600" />
              </div>
            </div>
            <div className="text-sm text-gray-600 mb-2">Overdue</div>
            <div className="text-3xl font-medium text-neutral-950 mb-1">{actionStats.overdue}</div>
            <div className="text-sm text-orange-600">
              Requires attention
            </div>
          </div>

          <div className="bg-white rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <div className="text-sm text-gray-600 mb-2">In Progress</div>
            <div className="text-3xl font-medium text-neutral-950 mb-1">{actionStats.inProgress}</div>
            <div className="text-sm text-blue-600">
              Currently active
            </div>
          </div>

          <div className="bg-white rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-12 h-12 rounded-lg bg-green-50 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div className="text-sm text-gray-600 mb-2">Completion Rate</div>
            <div className="text-3xl font-medium text-neutral-950 mb-1">{Math.round((actionStats.completed / actionStats.total) * 100)}%</div>
            <div className="text-sm text-green-600">
              {actionStats.completed} completed
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search actions..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
          <button className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm transition-colors">
            <Filter className="w-4 h-4" />
            Filter
          </button>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={showCompleted}
            onChange={(e) => setShowCompleted(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
          />
          <span className="text-gray-700">Show Completed</span>
        </label>
      </div>

      {/* Grouped View */}
      <div className="space-y-3">
        {Object.entries(groupedActions).map(([key, group]) => {
          const isExpanded = expandedGroups.has(key);
          const hasOverdue = group.actions.some(a => a.status === 'Overdue');
          const hasCritical = group.actions.some(a => a.priority === 'Critical');
          
          return (
            <div key={key} className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:border-blue-300 transition-colors">
              {/* Group Header - Clickable */}
              <div 
                onClick={() => toggleGroup(key)}
                className={`px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors ${
                  hasOverdue ? 'bg-red-50' : ''
                }`}
              >
                <div className="flex items-center gap-3 flex-1">
                  <div className={`w-8 h-8 ${getSourceColor(group.source)} rounded-lg flex items-center justify-center text-white flex-shrink-0`}>
                    {getSourceIcon(group.source)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-gray-900 truncate">{group.sourceDetails.name}</div>
                      {hasOverdue && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium flex-shrink-0">
                          Overdue
                        </span>
                      )}
                      {hasCritical && (
                        <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-medium flex-shrink-0">
                          Critical
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-600 mt-0.5">
                      {group.sourceDetails.date} {group.sourceDetails.time && `• ${group.sourceDetails.time}`}
                      {group.sourceDetails.from && ` • From: ${group.sourceDetails.from}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="px-3 py-1 bg-gray-100 rounded-full text-sm font-medium text-gray-700">
                      {group.actions.length} {group.actions.length === 1 ? 'action' : 'actions'}
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Group Actions - Collapsible */}
              {isExpanded && (
                <div className="border-t border-gray-200">
                  {group.actions.map((action, idx) => (
                    <div 
                      key={action.id} 
                      className={`px-4 py-3 flex items-center gap-3 hover:bg-blue-50 transition-colors cursor-pointer ${
                        idx !== group.actions.length - 1 ? 'border-b border-gray-100' : ''
                      } ${action.status === 'Overdue' ? 'bg-red-50/50' : ''}`}
                    >
                      {/* Priority Indicator */}
                      <div className="flex-shrink-0">
                        <div className={`w-2 h-2 rounded-full ${getPriorityDot(action.priority)}`} title={action.priority}></div>
                      </div>

                      {/* Status Checkbox */}
                      <div className="flex-shrink-0">
                        <input 
                          type="checkbox" 
                          checked={action.status === 'Completed'}
                          className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-2 focus:ring-green-500 cursor-pointer"
                          onChange={() => {}}
                        />
                      </div>

                      {/* Action Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2">
                          <span className="font-mono text-xs text-gray-500 flex-shrink-0">{action.id}</span>
                          <span className={`text-sm font-medium flex-1 ${
                            action.status === 'Completed' ? 'text-gray-500 line-through' : 'text-gray-900'
                          }`}>
                            {action.title}
                          </span>
                        </div>
                      </div>

                      {/* Assignee */}
                      <div className="flex items-center gap-1.5 text-sm text-gray-700 flex-shrink-0 min-w-[140px]">
                        <User className="w-3.5 h-3.5 text-gray-400" />
                        <span className="truncate">{action.assignedTo}</span>
                      </div>

                      {/* Status Badge */}
                      <div className="flex-shrink-0 min-w-[100px]">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs border ${getStatusColor(action.status)}`}>
                          {action.status === 'Completed' && <CheckCircle className="w-3 h-3" />}
                          {action.status === 'In Progress' && <Clock className="w-3 h-3" />}
                          {action.status === 'Overdue' && <AlertCircle className="w-3 h-3" />}
                          {action.status}
                        </span>
                      </div>

                      {/* Due Date */}
                      <div className={`flex items-center gap-1 text-sm flex-shrink-0 min-w-[100px] ${
                        isOverdue(action.dueDate, action.status) ? 'text-red-600 font-medium' : 'text-gray-600'
                      }`}>
                        <Clock className="w-3.5 h-3.5" />
                        {action.dueDate}
                      </div>

                      {/* View Details */}
                      <button className="flex-shrink-0 p-1.5 hover:bg-white rounded transition-colors">
                        <ExternalLink className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredActions.length === 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <CheckCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Action Items</h3>
          <p className="text-gray-600">
            {showCompleted ? 'No action items found.' : 'All action items are completed! Great work.'}
          </p>
        </div>
      )}
    </div>
  );
}